import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { meetWindowsAdmin, type MeetWindow, type MeetCoverage, type MeetLinkedExpert } from '@/lib/api/calendlyClient';
import ModuleLayout from '@/components/admin/ModuleLayout';
import { TrashBinIcon } from '@/icons';
import { toastError, toastSuccess } from '@/lib/toast';
import { showUndoToast } from '@/lib/undoToast';
import { LoadState } from "@/components/ui/LoadState"

const inputCls =
  'h-11 w-full rounded-lg border border-gray-300 px-3 text-[13px] text-gray-800 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/10 sm:h-10 dark:border-gray-700 dark:bg-gray-900 dark:text-white';

// cccd-event-scoped-experts-and-availability: bloques de fechas por evento
// ("Semana de asesorías #1"), compartidos por TODAS las categorías de ese
// evento — puramente informativos, para que el admin coordine con los
// expertos qué fechas/horas pueden atender. Nunca se cruza con Calendly ni
// restringe lo que un estudiante puede reservar.
export default function EventMeetWindowsPage() {
  const [searchParams] = useSearchParams();
  const scope = (searchParams.get('scope') === 'regional' ? 'regional' : 'national') as 'national' | 'regional';
  const id = searchParams.get('id') ?? '';

  const [windows, setWindows] = useState<MeetWindow[]>([]);
  const [loading, setLoading] = useState(false);
  const [coverageFor, setCoverageFor] = useState<MeetCoverage | null>(null);
  const [linkableExperts, setLinkableExperts] = useState<MeetLinkedExpert['categoryExpert'][]>([]);
  const [linking, setLinking] = useState(false);
  const [selectedExpertId, setSelectedExpertId] = useState('');

  const [newLabel, setNewLabel] = useState('');
  const [newStart, setNewStart] = useState('');
  const [newEnd, setNewEnd] = useState('');
  const [creating, setCreating] = useState(false);
  // Las fechas son 'YYYY-MM-DD': comparadas como texto respetan el orden cronológico.
  const invalidRange = !!newStart && !!newEnd && newEnd < newStart;

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      setWindows(await meetWindowsAdmin.list(scope === 'national' ? { eventId: id } : { regionalEventId: id }));
    } catch (err: any) {
      toastError(err?.response?.data?.error ?? 'Error al cargar los meets.');
    } finally {
      setLoading(false);
    }
  }, [scope, id]);

  useEffect(() => { void load(); }, [load]);

  async function onCreate() {
    if (!newLabel.trim() || !newStart || !newEnd || !id || invalidRange) return;
    setCreating(true);
    try {
      await meetWindowsAdmin.create({
        ...(scope === 'national' ? { eventId: id } : { regionalEventId: id }),
        label: newLabel.trim(),
        startDate: new Date(newStart).toISOString(),
        endDate: new Date(newEnd).toISOString(),
      });
      setNewLabel('');
      setNewStart('');
      setNewEnd('');
      await load();
      toastSuccess('Meet creado.');
    } catch (err: any) {
      toastError(err?.response?.data?.error ?? 'No se pudo crear el meet.');
    } finally {
      setCreating(false);
    }
  }

  function handleDelete(w: MeetWindow) {
    const snapshot = windows;
    setWindows((prev) => prev.filter((x) => x.id !== w.id));
    showUndoToast({
      message: `Meet "${w.label}" eliminado`,
      onUndo: () => setWindows(snapshot),
      onCommit: async () => {
        try {
          await meetWindowsAdmin.remove(w.id);
        } catch (err: any) {
          setWindows(snapshot);
          toastError(err?.response?.data?.error ?? 'No se pudo eliminar el meet.');
        }
      },
    });
  }

  async function onViewCoverage(w: MeetWindow) {
    try {
      const [coverage, linkable] = await Promise.all([
        meetWindowsAdmin.coverage(w.id),
        meetWindowsAdmin.linkableExperts(w.id),
      ]);
      setCoverageFor(coverage);
      setLinkableExperts(linkable);
      setSelectedExpertId('');
    } catch (err: any) {
      toastError(err?.response?.data?.error ?? 'No se pudo cargar la cobertura.');
    }
  }

  async function refreshCoverageAndLinkable(meetWindowId: string) {
    const [coverage, linkable] = await Promise.all([
      meetWindowsAdmin.coverage(meetWindowId),
      meetWindowsAdmin.linkableExperts(meetWindowId),
    ]);
    setCoverageFor(coverage);
    setLinkableExperts(linkable);
  }

  async function onLinkExpert() {
    if (!coverageFor || !selectedExpertId) return;
    setLinking(true);
    try {
      await meetWindowsAdmin.linkExpert(coverageFor.meetWindow.id, selectedExpertId);
      setSelectedExpertId('');
      await refreshCoverageAndLinkable(coverageFor.meetWindow.id);
      toastSuccess('Experto vinculado al meet.');
    } catch (err: any) {
      toastError(err?.response?.data?.error ?? 'No se pudo vincular el experto.');
    } finally {
      setLinking(false);
    }
  }

  async function onUnlinkExpert(categoryExpertId: string) {
    if (!coverageFor) return;
    try {
      await meetWindowsAdmin.unlinkExpert(coverageFor.meetWindow.id, categoryExpertId);
      await refreshCoverageAndLinkable(coverageFor.meetWindow.id);
      toastSuccess('Experto desvinculado del meet.');
    } catch (err: any) {
      toastError(err?.response?.data?.error ?? 'No se pudo desvincular el experto.');
    }
  }

  if (!id) {
    return (
      <ModuleLayout
        breadcrumbs={[{ label: 'SateLab', to: '/' }, { label: 'CCCD', to: '/cccd' }, 'Meets de evento']}
        title="Meets de evento"
        subtitle="Ve a 'Expertos por categoría', elige un evento, y usa el botón 'Meets de este evento' para llegar aquí."
        tabs={[]}
        metrics={[]}
      >
        <p className="text-sm text-gray-500 dark:text-gray-400">Falta el evento — vuelve desde la página de Expertos por categoría.</p>
      </ModuleLayout>
    );
  }

  return (
    <ModuleLayout
      breadcrumbs={[{ label: 'SateLab', to: '/' }, { label: 'CCCD', to: '/cccd' }, { label: 'Expertos por categoría', to: '/cccd/category-experts' }, 'Meets']}
      title="Meets del evento"
      subtitle="Bloques de fechas compartidos por todas las categorías del evento. Los expertos asignados declaran ellos mismos, en su 'Mi cuenta', qué fechas/horas pueden atender — esto es solo para tu coordinación interna, no afecta lo que un estudiante puede reservar en Calendly."
      tabs={[]}
      metrics={[]}
    >
      <div className="mb-5 rounded-2xl border border-dashed border-gray-300 bg-gray-25 p-5 dark:border-gray-700 dark:bg-white/[0.02]">
        <p className="mb-3 text-sm font-semibold text-gray-600 dark:text-gray-400">Crear meet</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <label htmlFor="meet-window-label" className="mb-1.5 block text-xs text-gray-600 dark:text-gray-400">Nombre</label>
            <input id="meet-window-label" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Semana de asesorías #1" className={inputCls} />
          </div>
          <div>
            <label htmlFor="meet-window-start" className="mb-1.5 block text-xs text-gray-600 dark:text-gray-400">Desde</label>
            <input id="meet-window-start" type="date" value={newStart} onChange={(e) => setNewStart(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label htmlFor="meet-window-end" className="mb-1.5 block text-xs text-gray-600 dark:text-gray-400">Hasta</label>
            <input id="meet-window-end" type="date" min={newStart || undefined} value={newEnd} onChange={(e) => setNewEnd(e.target.value)} aria-invalid={invalidRange} className={inputCls} />
          </div>
        </div>
        {invalidRange && (
          <p role="alert" className="mt-2 text-sm text-error-600 dark:text-error-400">
            La fecha "Hasta" no puede ser anterior a "Desde".
          </p>
        )}
        <button
          type="button"
          onClick={onCreate}
          disabled={!newLabel.trim() || !newStart || !newEnd || invalidRange || creating}
          className="mt-3 h-11 whitespace-nowrap sm:h-10 rounded-lg bg-brand-500 px-[18px] text-[13px] font-medium text-white hover:bg-brand-600 disabled:opacity-60"
        >
          {creating ? 'Creando…' : '+ Crear meet'}
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
        <div className="hidden grid-cols-[2fr_1fr_1fr_auto_auto] gap-3 border-b border-gray-100 bg-gray-25 px-5 py-3 sm:grid dark:border-gray-800 dark:bg-white/[0.02]">
          {['Nombre', 'Desde', 'Hasta', '', ''].map((h, i) => (
            <span key={i} className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{h || <span className="sr-only">Acciones</span>}</span>
          ))}
        </div>

        {loading ? (
          <LoadState />
        ) : windows.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-gray-500 dark:text-gray-400">Sin meets todavía para este evento.</p>
        ) : (
          windows.map((w) => (
            <div key={w.id} className="grid grid-cols-2 items-center gap-3 border-b sm:grid-cols-[2fr_1fr_1fr_auto_auto] border-gray-100 px-5 py-3 last:border-b-0 dark:border-gray-800">
              <span className="col-span-2 truncate text-[13px] text-gray-700 sm:col-span-1 dark:text-gray-300">{w.label}</span>
              <span className="text-[13px] text-gray-500 dark:text-gray-400"><span className="text-xs sm:hidden">Desde </span>{w.startDate.slice(0, 10)}</span>
              <span className="text-[13px] text-gray-500 dark:text-gray-400"><span className="text-xs sm:hidden">Hasta </span>{w.endDate.slice(0, 10)}</span>
              <button
                type="button"
                onClick={() => onViewCoverage(w)}
                className="min-h-11 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 sm:min-h-0 sm:whitespace-nowrap dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/[0.04]"
              >
                Expertos y cobertura
              </button>
              <button
                type="button"
                onClick={() => handleDelete(w)}
                title="Eliminar"
                aria-label={`Eliminar el meet ${w.label}`}
                className="flex size-11 items-center justify-center justify-self-end rounded-lg border border-gray-100 text-gray-500 hover:bg-error-50 hover:text-error-600 sm:size-8 dark:border-gray-800 dark:text-gray-400 dark:hover:bg-error-500/10"
              >
                <TrashBinIcon className="size-3.5" />
              </button>
            </div>
          ))
        )}
      </div>

      {coverageFor && (
        <div className="mt-5 rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">{coverageFor.meetWindow.label}</p>
            <button type="button" onClick={() => setCoverageFor(null)} className="min-h-11 px-2 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400">Cerrar</button>
          </div>

          <div className="mb-5">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Expertos vinculados a este meet
            </p>
            <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
              Solo un experto vinculado aquí puede ver este meet y declarar su disponibilidad — tener una asignación de categoría en el mismo evento no basta por sí sola.
            </p>
            {coverageFor.linkedExperts.length === 0 ? (
              <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">Ningún experto vinculado todavía.</p>
            ) : (
              <ul className="mb-3 space-y-2">
                {coverageFor.linkedExperts.map((link) => (
                  <li key={link.id} className="flex items-center justify-between rounded-lg bg-gray-25 px-3 py-2 text-[13px] dark:bg-white/[0.02]">
                    <span className="text-gray-700 dark:text-gray-300">{link.categoryExpert.user.name} · {link.categoryExpert.categorySlug}</span>
                    <button
                      type="button"
                      onClick={() => onUnlinkExpert(link.categoryExpertId)}
                      className="min-h-11 px-2 text-xs text-error-600 hover:underline"
                    >
                      Desvincular
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {linkableExperts.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <select
                  aria-label="Experto para vincular al meet"
                  value={selectedExpertId}
                  onChange={(e) => setSelectedExpertId(e.target.value)}
                  className={inputCls}
                >
                  <option value="">Elige un experto para vincular…</option>
                  {linkableExperts.map((ce) => (
                    <option key={ce.id} value={ce.id}>{ce.user.name} · {ce.categorySlug}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={onLinkExpert}
                  disabled={!selectedExpertId || linking}
                  className="h-11 whitespace-nowrap rounded-lg bg-brand-500 px-[18px] text-[13px] font-medium text-white hover:bg-brand-600 disabled:opacity-60 sm:h-10"
                >
                  {linking ? 'Vinculando…' : 'Vincular'}
                </button>
              </div>
            )}
          </div>

          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Cobertura declarada</p>
            {coverageFor.availability.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Ningún experto ha declarado disponibilidad todavía.</p>
            ) : (
              <ul className="space-y-2">
                {coverageFor.availability.map((entry) => (
                  <li key={entry.id} className="flex items-center justify-between rounded-lg bg-gray-25 px-3 py-2 text-[13px] dark:bg-white/[0.02]">
                    <span className="text-gray-700 dark:text-gray-300">{entry.categoryExpert.user.name} · {entry.categoryExpert.categorySlug}</span>
                    <span className="text-gray-500 dark:text-gray-400">{entry.date.slice(0, 10)} · {entry.startTime}–{entry.endTime}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </ModuleLayout>
  );
}
